// Display formatters for an ice time's wire shape: `date` is a date string and
// `start` is minutes since midnight, so the start instant is `date` + `start`.

import _ from 'lodash';
import dayjs from 'dayjs';

// The two fields that place a session in time. Kept separate from the place
// fields below so callers only declare what they actually read.
export interface ISessionTimeFields {
  date: string;
  start: number;
}

// The scheduled start as one instant, the basis of every ordering and
// "has it happened yet" check.
export const sessionStartInstant = ({ date, start }: ISessionTimeFields): dayjs.Dayjs =>
  dayjs(date).add(start || 0, 'minute');

// Most recent first. An unparseable date sorts last rather than throwing off
// the sessions around it.
export const sortSessionsNewestFirst = <T extends ISessionTimeFields>(sessions: T[]): T[] =>
  _.orderBy(sessions, (session) => {
    const instant = sessionStartInstant(session);
    return instant.isValid() ? instant.valueOf() : -Infinity;
  }, 'desc');

// The subset every caller passes — a full IAssessmentSession, a
// IPlayerAttendanceSession, or the session a Communication row is anchored to.
export interface ISessionPlaceFields {
  location?: string;
  area?: string;
}

// `duration` appends the end time; an unparseable date falls back to `date`.
// `withYear` is for surfaces listing past records, where the year disambiguates.
export const formatSessionDateTime = (
  date: string,
  start: number,
  duration?: number,
  withYear = false,
): string => {
  const instant = sessionStartInstant({ date, start });
  if (!instant.isValid()) return date;
  const base = instant.format(withYear ? 'ddd, MMM D, YYYY · h:mm A' : 'ddd, MMM D · h:mm A');
  return duration ? `${base} – ${instant.add(duration, 'minute').format('h:mm A')}` : base;
};

// The venue/area pair as one line; empty string when neither is set.
export const formatSessionPlace = (session: ISessionPlaceFields): string =>
  [session.location, session.area].filter(Boolean).join(' · ');
